/**
 * POSShiftCloseDialog — модал закриття зміни (Z-звіт).
 *
 * 1. POST /retail/cash-shift-close (backend агрегує підсумки + дає
 *    cash_difference).
 * 2. Якщо нестача → shortage_handling. Якщо лишок → інформаційно.
 * 3. Опційно: POST /cash-shift-close/{id}/fiscalize-z-report.
 *
 * Спрощений UX порівняно з бух-UI CashShiftCloseEditor — без вибору
 * shift_open (одна відкрита зміна на workstation).
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  retailCashShiftCloses,
  type CashShiftClose,
  type ShortageHandling,
  type CashCollectionMethod,
} from '@/api/retailCashShiftClose';
import type { CashShiftOpen } from '@/api/retailCashShiftOpen';

interface Props {
  shift: CashShiftOpen;
  onClosed: () => void;
  onClose: () => void;
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

const fmt = (v: string | number | null | undefined) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('uk-UA', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

export default function POSShiftCloseDialog({ shift, onClosed, onClose }: Props) {
  const qc = useQueryClient();
  const [actualCash, setActualCash] = useState('0');
  const [shortageHandling, setShortageHandling] = useState<ShortageHandling>('none');
  const [cashCollected, setCashCollected] = useState('0');
  const [cashMethod, setCashMethod] = useState<CashCollectionMethod>('none');
  const [error, setError] = useState<string | null>(null);
  const [closedDoc, setClosedDoc] = useState<CashShiftClose | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      retailCashShiftCloses.create({
        organization_id: shift.organization_id,
        shift_open_id: shift.id,
        actual_cash: actualCash,
        shortage_handling: shortageHandling,
        cash_collected: cashCollected,
        cash_collection_method: cashMethod,
      }),
    onSuccess: (doc) => {
      setClosedDoc(doc);
      qc.invalidateQueries({ queryKey: ['pos-current-shift'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  const fiscalize = useMutation({
    mutationFn: () => retailCashShiftCloses.fiscalizeZReport(closedDoc!.id),
    onSuccess: (r) => setClosedDoc(r.document),
    onError: (e) => setError(extractError(e)),
  });

  const diff = closedDoc?.cash_difference ? Number(closedDoc.cash_difference) : null;
  const hasShortage = diff !== null && diff < 0;
  const hasSurplus = diff !== null && diff > 0;

  return (
    <div className="pos-modal-overlay">
      <div className="pos-modal" style={{ maxWidth: 600 }}>
        <div className="pos-modal__header">
          <span>🔒 Закриття зміни</span>
          <button className="pos-modal__btn-secondary" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="pos-modal__body">
          {!closedDoc ? (
            <>
              <p>
                Зміна: <strong>{shift.document_number}</strong> (відкрита{' '}
                {new Date(shift.document_date).toLocaleString('uk-UA')})
              </p>
              <div className="pos-modal__field">
                <label>Фактично у касі (грн)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={actualCash}
                  onChange={(e) => setActualCash(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="pos-modal__field">
                <label>Якщо нестача — як обробити</label>
                <select
                  value={shortageHandling}
                  onChange={(e) => setShortageHandling(e.target.value as ShortageHandling)}
                >
                  <option value="none">Не застосовується</option>
                  <option value="forgive">Списати (Дт 947 — Кт 301)</option>
                  <option value="recover_from_cashier">Стягнути з касира</option>
                </select>
              </div>
              <div className="pos-modal__field">
                <label>Передано на інкасацію (грн)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={cashCollected}
                  onChange={(e) => setCashCollected(e.target.value)}
                />
              </div>
              <div className="pos-modal__field">
                <label>Метод інкасації</label>
                <select
                  value={cashMethod}
                  onChange={(e) => setCashMethod(e.target.value as CashCollectionMethod)}
                >
                  <option value="none">Не застосовується</option>
                  <option value="bank_deposit">Внесок у банк</option>
                  <option value="collector">Інкасатор</option>
                  <option value="transferred_to_next_shift">Передано наступній зміні</option>
                </select>
              </div>
              {error && (
                <div style={{ color: 'var(--danger)', padding: '0.5rem 0' }}>{error}</div>
              )}
            </>
          ) : (
            <>
              <h3 style={{ marginTop: 0 }}>✓ Зміна закрита</h3>
              <div className="pos-modal__amount-display">
                {fmt(closedDoc.total_sales_amount)} грн
              </div>
              <div className="pos-modal__field">
                Чеків: {closedDoc.total_sales_count} · Повернень:{' '}
                {closedDoc.total_returns_count}
              </div>
              <div className="pos-modal__field">
                Очікувана готівка: <strong>{fmt(closedDoc.expected_cash)}</strong>
              </div>
              <div className="pos-modal__field">
                Фактична готівка: <strong>{fmt(closedDoc.actual_cash)}</strong>
              </div>
              {diff !== null && (
                <div
                  className="pos-modal__field"
                  style={{
                    fontSize: '1.25rem',
                    color: hasShortage ? 'var(--danger)' : hasSurplus ? 'var(--success)' : 'inherit',
                  }}
                >
                  Розбіжність: {fmt(diff)} грн{' '}
                  {hasShortage && '(нестача)'}
                  {hasSurplus && '(лишок)'}
                </div>
              )}
              <div className="pos-modal__field">
                Z-звіт ПРРО: <strong>{closedDoc.fiscal_z_status}</strong>
                {closedDoc.fiscal_z_report_number && (
                  <> · № {closedDoc.fiscal_z_report_number}</>
                )}
              </div>
              {error && (
                <div style={{ color: 'var(--danger)', padding: '0.5rem 0' }}>{error}</div>
              )}
            </>
          )}
        </div>
        <div className="pos-modal__footer">
          {!closedDoc ? (
            <>
              <button className="pos-modal__btn-secondary" onClick={onClose}>
                Скасувати
              </button>
              <button
                className="pos-modal__btn-primary"
                onClick={() => submit.mutate()}
                disabled={submit.isPending}
              >
                {submit.isPending ? 'Закриваю...' : 'Закрити зміну'}
              </button>
            </>
          ) : (
            <>
              {closedDoc.fiscal_z_status !== 'fiscalized' && (
                <button
                  className="pos-modal__btn-secondary"
                  onClick={() => fiscalize.mutate()}
                  disabled={fiscalize.isPending}
                >
                  📋 Фіскалізувати Z-звіт
                </button>
              )}
              <button className="pos-modal__btn-primary" onClick={onClosed}>
                Готово
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
